import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Download, BellRing } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { usePwaInstall, enableNotifications, urlB64ToUint8Array } from "@/lib/pwa";
import { api } from "@/lib/api";
import { toast } from "sonner";

async function registerPush() {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
    const { data } = await api.get("/push/vapid-public-key");
    if (!data?.key) return;
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlB64ToUint8Array(data.key) });
    await api.post("/push/subscribe", { subscription: sub.toJSON() });
  } catch { /* ignore */ }
}

export default function PwaActions() {
  const { canInstall, promptInstall, isIos, isStandalone } = usePwaInstall();
  const [iosOpen, setIosOpen] = useState(false);
  const supported = typeof Notification !== "undefined";
  const [granted, setGranted] = useState(supported && Notification.permission === "granted");

  useEffect(() => { if (granted) registerPush(); }, [granted]);

  const doInstall = async () => {
    if (isIos) { setIosOpen(true); return; }
    const ok = await promptInstall();
    if (!ok) toast.message("Öppna webbläsarens meny och välj ”Installera app” / ”Lägg till på hemskärmen”.");
  };
  const doNotify = async () => {
    const p = await enableNotifications();
    if (p === "granted") { setGranted(true); await registerPush(); toast.success("Notiser aktiverade – du får aviseringar vid sjukanmälan m.m."); }
    else if (p === "unsupported") toast.error("Notiser stöds inte i denna webbläsare.");
    else toast.message("Tillåt notiser i webbläsaren för att få aviseringar.");
  };

  return (
    <>
      {!isStandalone && (canInstall || isIos) && (
        <Button variant="outline" size="sm" onClick={doInstall} data-testid="install-app-btn" className="gap-1.5">
          <Download className="h-4 w-4" /><span className="hidden sm:inline">Installera app</span>
        </Button>
      )}
      {supported && !granted && (
        <Button variant="ghost" size="icon" onClick={doNotify} data-testid="enable-notifications-btn" title="Aktivera notiser">
          <BellRing className="h-5 w-5" />
        </Button>
      )}
      <Dialog open={iosOpen} onOpenChange={setIosOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Installera på iPhone / iPad</DialogTitle></DialogHeader>
          <ol className="list-decimal space-y-2 pl-5 text-sm">
            <li>Tryck på <b>Dela</b>-ikonen längst ner i Safari.</li>
            <li>Välj <b>Lägg till på hemskärmen</b>.</li>
            <li>Bekräfta med <b>Lägg till</b>.</li>
          </ol>
          <p className="text-xs text-muted-foreground">Appen öppnas då i helskärm och kan visa notiser.</p>
        </DialogContent>
      </Dialog>
    </>
  );
}
